/**
 * Diagnostic des erreurs de chargement de modules (chunks JS/CSS).
 *
 * - Log console structuré (URL, statut HTTP réel via HEAD, type d'erreur)
 * - Événement `sunset:chunk-error` sur `window` pour brancher un monitoring
 */

export type ChunkErrorDetail = {
  url: string | null;
  message: string;
  source: "dynamic-import" | "script" | "unhandledrejection";
  status: number | null;
  statusText: string | null;
  attempt?: number | undefined;
  online: boolean;
  timestamp: number;
  href: string;
};

const CHUNK_ERROR_EVENT = "sunset:chunk-error";

const URL_RE = /https?:\/\/[^\s"')]+\.(?:js|mjs|css)(?:\?[^\s"')]*)?/i;

export function extractModuleUrl(input: unknown): string | null {
  const text =
    typeof input === "string"
      ? input
      : input instanceof Error
        ? `${input.message} ${input.stack ?? ""}`
        : String(input ?? "");
  return text.match(URL_RE)?.[0] ?? null;
}

function isChunkError(input: unknown): boolean {
  const msg =
    input instanceof Error ? input.message : typeof input === "string" ? input : "";
  return (
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i.test(
      msg,
    ) || (msg.length > 0 && URL_RE.test(msg))
  );
}

async function probe(url: string | null) {
  if (!url) return { status: null, statusText: null };
  try {
    const res = await fetch(url, { method: "HEAD", cache: "no-store" });
    return { status: res.status, statusText: res.statusText };
  } catch {
    return { status: null, statusText: "network-error" };
  }
}

/** Log + événement pour une erreur de chargement de module. */
export async function reportChunkError(
  error: unknown,
  meta: { source: ChunkErrorDetail["source"]; url?: string | null; attempt?: number },
) {
  if (typeof window === "undefined") return;

  const url = meta.url ?? extractModuleUrl(error);
  const { status, statusText } = await probe(url);

  const detail: ChunkErrorDetail = {
    url,
    message: error instanceof Error ? error.message : String(error),
    source: meta.source,
    status,
    statusText,
    attempt: meta.attempt,
    online: navigator.onLine,
    timestamp: Date.now(),
    href: window.location.href,
  };

  // eslint-disable-next-line no-console
  console.error(
    `[chunk-error] ${detail.source} ${status ?? "?"}${
      status === 403 ? " (accès refusé)" : status === 404 ? " (chunk obsolète)" : ""
    } → ${url ?? "URL inconnue"}`,
    detail,
  );

  window.dispatchEvent(new CustomEvent<ChunkErrorDetail>(CHUNK_ERROR_EVENT, { detail }));
}

/** Écouteurs globaux : scripts/CSS qui échouent et promesses rejetées. */
export function installChunkErrorLogging() {
  if (typeof window === "undefined") return;
  const w = window as Window & { __sunsetChunkLog?: boolean };
  if (w.__sunsetChunkLog) return;
  w.__sunsetChunkLog = true;

  const onResourceError = (event: Event) => {
    const target = event.target as HTMLScriptElement | HTMLLinkElement | null;
    if (!target) return;
    const url =
      (target as HTMLScriptElement).src || (target as HTMLLinkElement).href || null;
    if (!url || !URL_RE.test(url)) return;
    void reportChunkError(new Error(`Ressource non chargée: ${url}`), {
      source: "script",
      url,
    });
  };
  window.addEventListener("error", onResourceError, true);

  const onRejection = (event: PromiseRejectionEvent) => {
    if (!isChunkError(event.reason)) return;
    void reportChunkError(event.reason, { source: "unhandledrejection" });
  };
  window.addEventListener("unhandledrejection", onRejection);

  return () => {
    window.removeEventListener("error", onResourceError, true);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
